import { useMutation, useQuery } from '@apollo/client';
import moment from 'moment';
import { MouseEvent, useState } from 'react';
import { Accordion } from 'react-bootstrap';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import Spinner from 'react-bootstrap/Spinner';
import Table from 'react-bootstrap/Table';
import {
  DeleteRedeemCodeRedemptionMutation,
  DeleteRedeemCodeRedemptionMutationVariables,
  GetUserRedeemCodeHistoryQuery,
  GetUserRedeemCodeHistoryQueryVariables,
} from '../../../../../../__gqltypes__';
import { DELETE_USER_REDEEM_CODE_REDEMPTION, GET_USER_REDEEM_CODE_HISTORY } from '../../../graphql';

type Redemption = NonNullable<GetUserRedeemCodeHistoryQuery['user']>['redeemCodeRedemptions'][number];
type RedemptionReward = Redemption['rewards'][number];

const formatRewardSnapshot = (reward: RedemptionReward): string => {
  const amount = 'amount' in reward && typeof reward.amount === 'number' ? `${reward.amount} ` : '';
  let detail = '';
  if ('boostType' in reward && reward.boostType) {
    detail = ` (${reward.boostType})`;
  } else if ('category' in reward && reward.category) {
    detail = ` (${reward.category.id})`;
  } else if ('contentPack' in reward && reward.contentPack) {
    detail = ` (${reward.contentPack.id})`;
  } else if ('name' in reward && reward.name) {
    detail = ` (${reward.name})`;
  } else if ('id' in reward && reward.id) {
    detail = ` (${reward.id})`;
  }
  return `${amount}${reward.__typename}${detail}`;
};

const formatDate = (date: number | null | undefined): string =>
  date ? `${moment(date).utc().format('YYYY-MM-DD HH:mm')} UTC` : '-';

const RedeemCodeHistoryDisplay = ({ userId }: { userId: string }) => {
  const [expanded, setExpanded] = useState(false);
  const { data, loading, error, refetch } = useQuery<
    GetUserRedeemCodeHistoryQuery,
    GetUserRedeemCodeHistoryQueryVariables
  >(GET_USER_REDEEM_CODE_HISTORY, {
    variables: { userId },
    fetchPolicy: 'no-cache',
    skip: !userId,
  });
  const [deleteRedemption, { loading: deleting }] = useMutation<
    DeleteRedeemCodeRedemptionMutation,
    DeleteRedeemCodeRedemptionMutationVariables
  >(DELETE_USER_REDEEM_CODE_REDEMPTION);

  const redemptions = data?.user?.redeemCodeRedemptions ?? [];

  const handleRefresh = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (userId) {
      refetch();
    }
  };

  const handleDelete = (redemption: Redemption) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete the redemption of "${redemption.code}"? The account can then redeem it again.`)) {
      return;
    }
    deleteRedemption({ variables: { userId, redeemCodeId: redemption.redeemCodeId } })
      .then(() => refetch())
      .catch((mutationError) => {
        console.error(mutationError);
        // eslint-disable-next-line no-alert
        alert(`Failed to delete the redemption: ${mutationError?.message ?? 'unknown error'}`);
      });
  };

  return (
    <Card className="mb-2 shadow-sm border-0">
      <Card.Body className="py-2">
        <Accordion>
          <Accordion.Toggle as={Card.Title} variant="link" eventKey="0" onClick={() => setExpanded(!expanded)}>
            <div style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between' }}>
              Redeem code history
              <div>
                <Button
                  variant="link"
                  title="Refresh redeem code history"
                  disabled={loading}
                  onClick={handleRefresh}
                >
                  {loading ? <Spinner animation="border" size="sm" /> : <i className="fas fa-sync-alt" />}
                </Button>
                <Button variant="link">
                  {!expanded && <i className="fas fa-chevron-down" />}
                  {expanded && <i className="fas fa-chevron-up" />}
                </Button>
              </div>
            </div>
          </Accordion.Toggle>
          <Accordion.Collapse eventKey="0">
            <>
              {loading && (
                <div className="m-2">
                  <Spinner animation="border" />
                </div>
              )}
              {error && <div className="m-1 text-danger">{error.message}</div>}
              {!loading &&
                !error &&
                (redemptions.length ? (
                  <div className="table-responsive" style={{ maxHeight: '100vh' }}>
                    <Table striped bordered size="sm">
                      <thead>
                        <tr>
                          <th>Code</th>
                          <th>Rewards</th>
                          <th>Redeemed at (UTC)</th>
                          <th />
                        </tr>
                      </thead>
                      <tbody>
                        {redemptions.map((redemption) => (
                          <tr key={`${redemption.redeemCodeId}`}>
                            <td>{redemption.code}</td>
                            <td>{redemption.rewards.map(formatRewardSnapshot).join(', ') || '-'}</td>
                            <td>{redemption.redeemedAt ? formatDate(redemption.redeemedAt) : 'Incomplete (retry allowed)'}</td>
                            <td className="text-center">
                              <Button
                                variant="outline-danger"
                                size="sm"
                                title="QA: delete this redemption so the account can redeem the code again"
                                disabled={deleting}
                                onClick={() => handleDelete(redemption)}
                              >
                                <i className="fas fa-trash-alt" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                ) : (
                  <div className="m-1">No redeem code redemptions.</div>
                ))}
            </>
          </Accordion.Collapse>
        </Accordion>
      </Card.Body>
    </Card>
  );
};

export default RedeemCodeHistoryDisplay;
